/**
 * Vibrantline: tabs for grouped variant pills + unavailable UX helpers.
 * See snippets/vl-option-groups.liquid and product-variant-options.liquid.
 *
 * data-vl-unavailable-mode on <variant-selects>:
 *   hide — hide missing SKU combos; strikethrough only true sold-outs; helper line
 *   soft — muted style for missing; click swaps other options to a valid combo
 */
if (!customElements.get('vl-option-tabs')) {
  customElements.define(
    'vl-option-tabs',
    class VlOptionTabs extends HTMLElement {
      connectedCallback() {
        if (this.vlBound) return;
        this.vlBound = true;
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
      }

      get tabs() {
        return Array.from(this.querySelectorAll(':scope > [role="tablist"] > [role="tab"]'));
      }

      onClick(event) {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !this.contains(tab)) return;
        event.preventDefault();
        this.select(tab);
      }

      onKeydown(event) {
        const tab = event.target.closest('[role="tab"]');
        if (!tab) return;
        const tabs = this.tabs;
        const index = tabs.indexOf(tab);
        let next = null;
        if (event.key === 'Tab' && !event.shiftKey) {
          const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
          if (panel && !panel.querySelector('input[type="radio"]:checked')) {
            const first =
              panel.querySelector(
                'input[type="radio"]:not(.disabled):not(.vl-option--missing):not(.vl-option--soldout)'
              ) || panel.querySelector('input[type="radio"]:not(.vl-option--missing)');
            if (first) {
              event.preventDefault();
              first.focus();
            }
          }
          return;
        }
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = tabs[(index + 1) % tabs.length];
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = tabs[(index - 1 + tabs.length) % tabs.length];
            break;
          case 'Home':
            next = tabs[0];
            break;
          case 'End':
            next = tabs[tabs.length - 1];
            break;
          default:
            return;
        }
        event.preventDefault();
        this.select(next, true);
      }

      select(activeTab, focus = false) {
        this.tabs.forEach((tab) => {
          const active = tab === activeTab;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
          if (panel) panel.hidden = !active;
        });
        if (focus) activeTab.focus();
        activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
  );
}

(function () {
  const DIM_RE = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i;

  function rootSelects(node) {
    return node?.closest?.('variant-selects') || document.querySelector('variant-selects');
  }

  function modeOf(selects) {
    return selects?.dataset?.vlUnavailableMode || '';
  }

  function dimensionLabel(value) {
    const m = String(value || '').match(DIM_RE);
    return m ? `${m[1]}×${m[2]}` : String(value || '').trim();
  }

  function uniqueDimensions(values) {
    const seen = new Set();
    const out = [];
    values.forEach((v) => {
      const label = dimensionLabel(v);
      if (!label || seen.has(label)) return;
      seen.add(label);
      out.push(label);
    });
    return out;
  }

  function formatList(items) {
    if (items.length <= 1) return items[0] || '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  }

  function otherOptionLabels(selects, sizeFieldset) {
    const labels = [];
    selects.querySelectorAll('fieldset.product-form__input--pill').forEach((fs) => {
      if (fs === sizeFieldset) return;
      fs.querySelectorAll('input[type="radio"]:not(:checked)').forEach((input) => {
        if (input.classList.contains('vl-option--missing')) return;
        if (input.value) labels.push(input.value);
      });
    });
    return labels;
  }

  function otherOptionGroupName(selects, sizeFieldset) {
    for (const fs of selects.querySelectorAll('fieldset.product-form__input--pill')) {
      if (fs === sizeFieldset) continue;
      const legend = fs.querySelector('legend.form__label, .form__label');
      if (!legend) continue;
      // Prefer the option name before any ": selected" suffix
      const raw = (legend.textContent || '').trim();
      const name = raw.split(':')[0].trim();
      if (name) return name;
    }
    return '';
  }

  function updateHelpers(selects) {
    if (!selects || modeOf(selects) !== 'hide') return;
    selects.querySelectorAll('[data-vl-option-helper]').forEach((helper) => {
      const fieldset = helper.closest('fieldset');
      if (!fieldset) return;

      // Advertise sizes that belong to OTHER option values (hidden for current selection)
      const missingInputs = Array.from(
        fieldset.querySelectorAll('input[type="radio"].vl-option--missing')
      );
      const dims = uniqueDimensions(missingInputs.map((i) => i.value));
      if (!dims.length) {
        helper.textContent = '';
        return;
      }

      const others = otherOptionLabels(selects, fieldset);
      if (others.length === 1) {
        helper.textContent = `More sizes available with ${others[0]}: ${formatList(dims)}`;
      } else if (others.length > 1) {
        helper.textContent = `More sizes available with ${formatList(others)}: ${formatList(dims)}`;
      } else {
        const group = otherOptionGroupName(selects, fieldset);
        helper.textContent = group
          ? `More product options available in: ${group}`
          : `More sizes available: ${formatList(dims)}`;
      }
    });
  }

  function firstSelectable(fieldset) {
    const candidates = Array.from(
      fieldset.querySelectorAll(
        'input[type="radio"]:not(.vl-option--missing):not(.vl-option--soldout):not(.disabled)'
      )
    );
    const visible = candidates.filter((input) => {
      const panel = input.closest('.vl-tabs__panel');
      return !panel || !panel.hidden;
    });
    return visible[0] || candidates[0] || null;
  }

  function ensureValidSelection(selects) {
    if (!selects || modeOf(selects) !== 'hide') return false;
    let changed = false;
    selects.querySelectorAll('fieldset.product-form__input--pill').forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!checked) {
        const first = firstSelectable(fieldset);
        if (first) {
          first.checked = true;
          changed = true;
          first.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      if (checked.classList.contains('vl-option--missing')) {
        const first = firstSelectable(fieldset);
        if (first && first !== checked) {
          checked.checked = false;
          first.checked = true;
          changed = true;
          first.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
    return changed;
  }

  function showSoftToast(selects, message) {
    let toast = selects.querySelector('[data-vl-soft-toast]');
    if (!toast) {
      toast = document.createElement('p');
      toast.className = 'vl-option-helper vl-option-helper--toast';
      toast.setAttribute('data-vl-soft-toast', '');
      toast.setAttribute('aria-live', 'polite');
      selects.appendChild(toast);
    }
    toast.textContent = message;
    clearTimeout(toast._vlToastTimer);
    toast._vlToastTimer = setTimeout(() => {
      if (toast.textContent === message) toast.textContent = '';
    }, 3200);
  }

  function applySoftSwap(input, event) {
    const selects = rootSelects(input);
    if (!selects || modeOf(selects) !== 'soft') return false;
    if (!input.classList.contains('vl-option--soft') || input.dataset.vlState !== 'missing') return false;

    const swap = (input.dataset.vlSwapOptionValues || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!swap.length) return false;

    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();

    const allRadios = Array.from(selects.querySelectorAll('input[type="radio"][data-option-value-id]'));
    const byId = new Map(allRadios.map((r) => [r.dataset.optionValueId, r]));
    let switchedLabel = '';
    swap.forEach((id) => {
      const radio = byId.get(id);
      if (!radio) return;
      if (radio.dataset.optionName !== input.dataset.optionName) {
        switchedLabel = radio.value;
      }
      radio.checked = true;
    });

    if (switchedLabel) {
      showSoftToast(selects, `That size is for ${switchedLabel} — switched for you.`);
    }

    const sizeRadio = byId.get(input.dataset.optionValueId) || input;
    sizeRadio.checked = true;
    sizeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function refresh(selects) {
    if (!selects) return;
    const mode = modeOf(selects);
    if (mode === 'hide') {
      // Avoid re-entry loops: only auto-select once per paint
      if (!selects._vlEnsuring) {
        selects._vlEnsuring = true;
        try {
          ensureValidSelection(selects);
        } finally {
          selects._vlEnsuring = false;
        }
      }
      updateHelpers(selects);
    }
  }

  function boot() {
    document.querySelectorAll('variant-selects[data-vl-unavailable-mode]').forEach(refresh);

    document.addEventListener(
      'click',
      (event) => {
        const label = event.target.closest?.('label');
        const input = event.target.closest?.('input.vl-option--soft') ||
          (label?.htmlFor ? document.getElementById(label.htmlFor) : null);
        if (input?.classList?.contains('vl-option--soft')) {
          applySoftSwap(input, event);
        }
      },
      true
    );

    if (window.subscribe && window.PUB_SUB_EVENTS?.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        requestAnimationFrame(() => {
          document.querySelectorAll('variant-selects[data-vl-unavailable-mode]').forEach(refresh);
        });
      });
    }

    // Section HTML swaps replace <variant-selects>; observe product-info
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('variant-selects[data-vl-unavailable-mode]')) refresh(node);
          node.querySelectorAll?.('variant-selects[data-vl-unavailable-mode]').forEach(refresh);
        });
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
